import {
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogSubmit,
  DialogTitle
} from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import { type Dispatch, type SetStateAction, useState } from 'react';

import type { DuplicateProcessOptions } from '@/mobx/store/process.store';
import { newError } from '@/services/errors/errors';

type Props = {
  setOpen: Dispatch<SetStateAction<boolean>>;
  processName: string;
  onDuplicate: (options: DuplicateProcessOptions) => Promise<void>;
};

export const DuplicateDialogContent = ({
  setOpen,
  processName,
  onDuplicate
}: Props) => {
  const [name, setName] = useState(`${processName} - Copy`);
  const [copyStructure, setCopyStructure] = useState(true);
  const [copyRepoData, setCopyRepoData] = useState(false);

  const canSubmit = name.trim().length > 0;

  const onCancel = () => {
    setOpen(false);
  };

  return (
    <DialogContent className="w-1/2" onClick={e => e.stopPropagation()}>
      <DialogHeader>
        <DialogTitle>Duplicate process</DialogTitle>
        <DialogDescription>
          Configure the options for the duplicated process.
        </DialogDescription>
      </DialogHeader>
      <section className="flex flex-col gap-6 p-5">
        <div className="flex flex-col gap-2">
          <Label htmlFor="duplicate-name">Name</Label>
          <Input
            id="duplicate-name"
            onChange={e => setName(e.target.value)}
            placeholder="Process name"
            value={name}
          />
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <Label className="cursor-pointer" htmlFor="copy-structure">
              Copy database structure
            </Label>
            <Switch
              checked={copyStructure}
              id="copy-structure"
              onCheckedChange={checked => {
                setCopyStructure(checked);
                if (!checked) {
                  setCopyRepoData(false);
                }
              }}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label
              className={
                copyStructure
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed opacity-50'
              }
              htmlFor="copy-repo-data"
            >
              Copy database rows
            </Label>
            <Switch
              checked={copyRepoData}
              disabled={!copyStructure}
              id="copy-repo-data"
              onCheckedChange={setCopyRepoData}
            />
          </div>
        </div>
      </section>
      <DialogFooter>
        <DialogCancel onClick={onCancel}>Cancel</DialogCancel>
        <DialogSubmit
          disabled={!canSubmit}
          onClick={e => {
            if (!canSubmit) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            onDuplicate({
              name: name.trim(),
              copyStructure,
              copyRepoData
            }).catch(err => {
              newError('DUPL-1', err, true);
            });
            setOpen(false);
          }}
        >
          Duplicate
        </DialogSubmit>
      </DialogFooter>
    </DialogContent>
  );
};
